import React from "react";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";

import ProjectSizeButtonGroup from "../../ProjectSizeButtonGroup";
import type { ProjectAreaPreview } from "../useProjectAreaPreview";
import type { ProjectSize, StepProps } from "../types";

interface LocationSizeControlsProps extends StepProps {
  area: ProjectAreaPreview;
  /** A place search, shown above the instructions (the listing's new-project panel). */
  searchSlot?: React.ReactNode;
}

/**
 * The Location & Size step's controls: where to click, the project size,
 * the property boundary preview status and tile expansion. The map they
 * drive is the caller's: beside them in the full-page wizard, the listing
 * map in the new-project flow.
 */
const LocationSizeControls: React.FC<LocationSizeControlsProps> = ({ data, onUpdateData, area, searchSlot }) => {
  const { selectedLatLng } = data;
  const { canExpand, expansionTiles, selectedTiles, sourcePreview, propertyFileReady } = area;

  const propertySourceCount = data.selectedPropertySources.length;
  const sourcePreviewMessage = !selectedLatLng
    ? "Pick a location to preview the parcels in the project area"
    : sourcePreview.loading
      ? "Loading parcels for the project area…"
      : sourcePreview.count === 0 && sourcePreview.errors.length === 0
        ? "No parcels found in the project area"
        : `${sourcePreview.count.toLocaleString()} parcel${sourcePreview.count === 1 ? "" : "s"} shown on the map${
            sourcePreview.truncated ? " (first part only; the full set loads when the project is created)" : ""
          }`;
  const sourcePreviewColor =
    sourcePreview.loading || !selectedLatLng
      ? "#666"
      : sourcePreview.errors.length > 0
        ? "#d32f2f"
        : sourcePreview.count > 0
          ? "#4caf50"
          : "#666";

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: "#1a1a1a",
          textAlign: "center",
        }}
      >
        Define your project area and scope
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 4,
          color: "#666",
        }}
      >
        Define your project area and scope
      </Typography>

      {searchSlot && <Box sx={{ mb: 2, width: "100%" }}>{searchSlot}</Box>}

      {/* Instructions */}
      <Box sx={{ mb: 4, width: "100%" }}>
        <Typography
          variant="body2"
          sx={{
            color: "#666",
            fontStyle: "italic",
            textAlign: "center",
            p: 3,
            backgroundColor: "white",
            borderRadius: 2,
            border: "1px dashed #e0e0e0",
          }}
        >
          {selectedLatLng
            ? "Location selected — click anywhere on the map to move it"
            : "Click on the map to select your project location"}
        </Typography>
      </Box>

      {/* Project Size */}
      <Box sx={{ mb: 4, width: "100%" }}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: "#1a1a1a",
            textAlign: "center",
          }}
        >
          Project Size
        </Typography>
        <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <CardContent sx={{ p: 3 }}>
            <ProjectSizeButtonGroup
              projectSize={data.projectSize}
              onProjectSizeChange={(size: string) => onUpdateData({ projectSize: size as ProjectSize })}
              showAspectRatio={false}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Property boundaries & files */}
      {(data.fuelDisruptionFile || data.propertyFiles || propertySourceCount > 0) && (
        <Box sx={{ mb: 4, width: "100%" }}>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: "#1a1a1a",
              textAlign: "center",
            }}
          >
            Property Boundaries &amp; Files
          </Typography>
          <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              {data.fuelDisruptionFile && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Fuel Disruption File: {data.fuelDisruptionFile.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#4caf50",
                      fontStyle: "italic",
                    }}
                  >
                    Ready - file will be uploaded when you create the project
                  </Typography>
                </Box>
              )}
              {data.propertyFiles && (
                <Box sx={{ mb: propertySourceCount > 0 ? 2 : 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Property Files: 1 file(s)
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: propertyFileReady ? "#4caf50" : "#666",
                      fontStyle: "italic",
                    }}
                  >
                    {propertyFileReady ? "Ready - Preview available on map" : "Pending processing"}
                  </Typography>
                </Box>
              )}
              {propertySourceCount > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Property Boundary Sources: {data.selectedPropertySources.map((s) => s.name).join(", ")}
                  </Typography>
                  <Typography variant="caption" sx={{ color: sourcePreviewColor, fontStyle: "italic", display: "block" }}>
                    {sourcePreviewMessage}
                  </Typography>
                  {sourcePreview.errors.map((err) => (
                    <Typography key={err} variant="caption" sx={{ color: "#d32f2f", display: "block" }}>
                      Preview unavailable — {err}
                    </Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Expand Project Area — grow the square tile-by-tile, the same way the
          project-expand feature works. Gated behind the expansion capability. */}
      {canExpand && (
        <Box sx={{ mb: 4, width: "100%" }}>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: "#1a1a1a",
              textAlign: "center",
            }}
          >
            Expand Project Area
          </Typography>
          <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 3, color: "#666" }}>
                {selectedLatLng
                  ? "Use the + buttons on the map to grow the area north, south, east or west. Use − to remove an added tile."
                  : "Select a location on the map to place your project square, then expand it tile-by-tile."}
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 2,
                  backgroundColor: expansionTiles.length > 0 ? "#e8f5e8" : "#f0f0f0",
                  borderRadius: 2,
                  border: `1px solid ${expansionTiles.length > 0 ? "#00d4ad" : "#e0e0e0"}`,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: expansionTiles.length > 0 ? "#00b89c" : "#666",
                  }}
                >
                  {selectedTiles.length === 1 ? "Single tile" : `${selectedTiles.length} tiles selected`}
                </Typography>
                <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                  One project will be created and expanded to cover every tile
                </Typography>
              </Box>
              {expansionTiles.length > 0 && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => onUpdateData({ expansionTiles: [] })}
                  sx={{
                    mt: 2,
                    borderColor: "#e0e0e0",
                    color: "#666",
                    "&:hover": { borderColor: "#ccc", backgroundColor: "#f5f5f5" },
                  }}
                >
                  Reset to single tile
                </Button>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );
};

export default LocationSizeControls;
